import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ExportRequestService } from '../../services/ExportRequestService/export-request';
import { ProjectService } from '../../services/ProjectService/project';
import { AuthService } from '../../services/AuthService/auth';
import { CountrySelect } from '../../shared/components/country-select/country-select';
import {
  ChainPreviewResponse,
  CompanySummary,
  ExportRequestChainSubmissionResponse,
  ExportRequestResponse,
  MiddlewareNotificationResponse,
  XmlPreviewResponse,
} from '../../models/export-request.dto';
import { extractBlobErrorMessage, extractErrorMessage } from '../../shared/error-utils';
import { triggerBlobDownload } from '../../shared/file-download';
import { canViewPlatformExportRequests } from '../../shared/permissions';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { DeclineExportRequestDialog } from '../../dialogs/decline-export-request-dialog/decline-export-request-dialog';

function formatPreviewValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
}

@Component({
  selector: 'app-export-request-detail',
  imports: [CommonModule, FormsModule, RouterLink, StatusBadge, ErrorBanner, CountrySelect],
  templateUrl: './export-request-detail.html',
  styleUrl: './export-request-detail.scss',
})
export class ExportRequestDetail implements OnInit {
  requestId = '';
  request = signal<ExportRequestResponse | null>(null);

  isLoading = signal(false);
  errorMessage = signal('');
  notFound = signal(false);

  isAccepting = signal(false);

  chainSubmissions = signal<ExportRequestChainSubmissionResponse[]>([]);
  isLoadingChainSubmissions = signal(false);
  retryingSubmissionId = signal<string | null>(null);
  chainSubmissionsError = signal('');

  chainPreview = signal<ChainPreviewResponse | null>(null);
  isLoadingPreview = signal(false);
  previewError = signal('');

  notifications = signal<MiddlewareNotificationResponse[]>([]);
  isLoadingNotifications = signal(false);
  notificationsError = signal('');

  // A country any participant can pick and check — never auto-run, since unlike the chain
  // preview above this isn't gating a decision, it's "show me the document this would become".
  xmlCountryCode = '';
  xmlPreview = signal<XmlPreviewResponse | null>(null);
  isLoadingXmlPreview = signal(false);
  xmlPreviewError = signal('');

  // Downloads a real file over the same country choice above, independent of the rendered preview
  // — a caller can download without ever clicking "Render XML" first.
  isDownloadingXml = signal(false);
  isDownloadingJson = signal(false);
  downloadError = signal('');

  private companyDirectory = signal<Map<string, CompanySummary>>(new Map());

  constructor(
    private route: ActivatedRoute,
    private exportRequestService: ExportRequestService,
    private projectService: ProjectService,
    private auth: AuthService,
    private dialog: MatDialog
  ) {}

  get backLink(): string {
    return canViewPlatformExportRequests(this.auth.getSession()?.role) ? '/admin/export-requests' : '/export-requests';
  }

  get canDecide(): boolean {
    const req = this.request();
    const companyId = this.auth.getSession()?.companyId;
    return !!req && req.status === 'PENDING' && req.importerCompanyId === companyId;
  }

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.notFound.set(false);

    this.exportRequestService.get(this.requestId).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.request.set(res.data);
        this.loadCompanyDirectory(res.data.projectId);
        this.loadChainSubmissions();
        this.loadNotifications();
        if (this.canDecide) {
          this.loadChainPreview();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        if (err.status === 404) {
          this.notFound.set(true);
          return;
        }
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load export request.'));
      },
    });
  }

  companyLabel(companyId: string): string {
    const company = this.companyDirectory().get(companyId);
    return company ? `${company.name} (${company.countryCode})` : '—';
  }

  private loadCompanyDirectory(projectId: string): void {
    this.projectService.listParticipants(projectId).subscribe({
      next: (res) => {
        const directory = new Map<string, CompanySummary>();
        for (const p of res.data) {
          directory.set(p.companyId, { id: p.companyId, name: p.companyName, countryCode: p.countryCode });
        }
        this.companyDirectory.set(directory);
      },
      error: () => {},
    });
  }

  loadChainSubmissions(): void {
    this.isLoadingChainSubmissions.set(true);
    this.chainSubmissionsError.set('');

    this.exportRequestService.chainSubmissions(this.requestId).subscribe({
      next: (res) => {
        this.isLoadingChainSubmissions.set(false);
        this.chainSubmissions.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingChainSubmissions.set(false);
        this.chainSubmissionsError.set(extractErrorMessage(err, 'Failed to load chain submissions.'));
      },
    });
  }

  loadNotifications(): void {
    this.isLoadingNotifications.set(true);
    this.notificationsError.set('');

    this.exportRequestService.notifications(this.requestId).subscribe({
      next: (res) => {
        this.isLoadingNotifications.set(false);
        this.notifications.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingNotifications.set(false);
        this.notificationsError.set(extractErrorMessage(err, 'Failed to load destination notifications.'));
      },
    });
  }

  loadChainPreview(): void {
    this.isLoadingPreview.set(true);
    this.previewError.set('');

    this.exportRequestService.chainPreview(this.requestId).subscribe({
      next: (res) => {
        this.isLoadingPreview.set(false);
        this.chainPreview.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingPreview.set(false);
        this.previewError.set(extractErrorMessage(err, 'Failed to load chain preview.'));
      },
    });
  }

  loadXmlPreview(): void {
    this.isLoadingXmlPreview.set(true);
    this.xmlPreviewError.set('');

    this.exportRequestService.xmlPreview(this.requestId, this.xmlCountryCode || undefined).subscribe({
      next: (res) => {
        this.isLoadingXmlPreview.set(false);
        this.xmlPreview.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingXmlPreview.set(false);
        this.xmlPreviewError.set(extractErrorMessage(err, 'Failed to render the XML preview.'));
      },
    });
  }

  downloadXml(): void {
    this.isDownloadingXml.set(true);
    this.downloadError.set('');

    this.exportRequestService.downloadXml(this.requestId, this.xmlCountryCode || undefined).subscribe({
      next: (res) => {
        this.isDownloadingXml.set(false);
        triggerBlobDownload(res, `export-${this.requestId}.xml`);
      },
      error: (err: HttpErrorResponse) => {
        this.isDownloadingXml.set(false);
        extractBlobErrorMessage(err, 'Failed to download the XML file.').then((message) =>
          this.downloadError.set(message)
        );
      },
    });
  }

  downloadJson(): void {
    this.isDownloadingJson.set(true);
    this.downloadError.set('');

    this.exportRequestService.downloadJson(this.requestId, this.xmlCountryCode || undefined).subscribe({
      next: (res) => {
        this.isDownloadingJson.set(false);
        triggerBlobDownload(res, `export-${this.requestId}.json`);
      },
      error: (err: HttpErrorResponse) => {
        this.isDownloadingJson.set(false);
        extractBlobErrorMessage(err, 'Failed to download the JSON file.').then((message) =>
          this.downloadError.set(message)
        );
      },
    });
  }

  copyXml(): void {
    const xml = this.xmlPreview()?.xml;
    if (xml) {
      navigator.clipboard?.writeText(xml);
    }
  }

  get previewRecordEntries(): [string, string][] {
    const record = this.chainPreview()?.record;
    if (!record) {
      return [];
    }
    return Object.entries(record).map(([key, value]) => [key, formatPreviewValue(value)]);
  }

  canRetry(submission: ExportRequestChainSubmissionResponse): boolean {
    const companyId = this.auth.getSession()?.companyId;
    return submission.status === 'FAILED' && submission.companyId === companyId;
  }

  retry(submission: ExportRequestChainSubmissionResponse): void {
    this.retryingSubmissionId.set(submission.id);
    this.chainSubmissionsError.set('');

    this.exportRequestService.retryChainSubmission(this.requestId).subscribe({
      next: () => {
        this.retryingSubmissionId.set(null);
        this.loadChainSubmissions();
      },
      error: (err: HttpErrorResponse) => {
        this.retryingSubmissionId.set(null);
        this.chainSubmissionsError.set(extractErrorMessage(err, 'Failed to retry chain submission.'));
      },
    });
  }

  accept(): void {
    this.isAccepting.set(true);
    this.errorMessage.set('');

    this.exportRequestService.accept(this.requestId).subscribe({
      next: () => {
        this.isAccepting.set(false);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.isAccepting.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to accept export request.'));
      },
    });
  }

  openDeclineDialog(): void {
    this.dialog
      .open(DeclineExportRequestDialog, { width: '480px', data: { exportRequestId: this.requestId } })
      .afterClosed()
      .subscribe((declined) => {
        if (declined) {
          this.load();
        }
      });
  }
}
